import { ChangeEvent, useCallback, useRef } from "react";
import {
  ControllerFieldState,
  ControllerRenderProps,
  FieldPath,
  FieldPathValue,
  FieldValues,
  UseControllerProps,
  UseFormStateReturn,
  useController,
} from "react-hook-form";

export type FileUploadControllerProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
> = {
  render: (props: {
    field: Omit<ControllerRenderProps<TFieldValues, TName>, "value"> &
      React.InputHTMLAttributes<HTMLInputElement>;
    fieldState: ControllerFieldState;
    formState: UseFormStateReturn<TFieldValues>;
    value: FieldPathValue<TFieldValues, TName>;
    select: () => void;
    remove: () => void;
  }) => React.ReactElement;
  uploadFiles: (files: FileList) => Promise<string>;
  deleteReference?: (reference: string) => Promise<void>;
} & UseControllerProps<TFieldValues, TName, TTransformedValues>;

export const FileUploadController = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
>({
  render,
  uploadFiles,
  deleteReference,
  ...useControllerProps
}: FileUploadControllerProps<TFieldValues, TName, TTransformedValues>) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const controller = useController(useControllerProps);
  const field = controller.field;

  const onChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files || files.length === 0) return;
      const oldValue = field.value;
      const result = await uploadFiles(files);
      field.onChange(result);
      if (oldValue && deleteReference) {
        return deleteReference(oldValue);
      }
    },
    [field, uploadFiles, deleteReference],
  );

  return render({
    ...controller,
    value: field.value,
    field: {
      ...field,
      type: "file",
      // It's not legal to set a `value` attribute for `<input type="file" />`,
      // so we forcefully ensure that this attribute is undefined.
      // To read the value set in React Hook Form's state, use the top-level `value` prop.
      value: undefined,
      onChange,
      ref: (instance) => {
        field.ref(instance);
        inputRef.current = instance;
      },
    },
    select: () => inputRef.current?.click(),
    remove: async () => {
      field.onChange("");
      if (inputRef.current) {
        inputRef.current.value = "";
      }
      if (deleteReference) {
        await deleteReference(field.value);
      }
    },
  });
};
